import React, { useEffect, useRef, useState } from 'react'

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const services = [
  {
    title: 'Personal Insurance',
    description: 'Tailored coverage for individuals and families — home, valuables, and peace of mind bundled into one smart plan.',
    icon: '🏠',
    tag: 'Individuals',
    image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&q=80',
    color: '#c41e3a',
  },
  {
    title: 'Business Insurance',
    description: 'End-to-end risk management for enterprises of every size — from start-ups to multinationals.',
    icon: '💼',
    tag: 'Corporate',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    color: '#8b0000',
  },
  {
    title: 'Auto Insurance',
    description: 'Comprehensive motor cover with roadside assistance, third-party liability, and fleet options.',
    icon: '🚗',
    tag: 'Motor',
    image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&q=80',
    color: '#c41e3a',
  },
  {
    title: 'Health Insurance',
    description: 'Individual and group health plans with access to top-tier hospitals and specialist care worldwide.',
    icon: '🏥',
    tag: 'Wellness',
    image: 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=800&q=80',
    color: '#d32f2f',
  },
  {
    title: 'Life Insurance',
    description: 'Whole life, term, and investment-linked policies to secure your family\'s tomorrow, today.',
    icon: '🛡️',
    tag: 'Family',
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191011?w=800&q=80',
    color: '#ef5350',
  },
  {
    title: 'Property Insurance',
    description: 'Guard your real estate portfolio against fire, flood, theft, and unforeseen structural damage.',
    icon: '🏢',
    tag: 'Real Estate',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    color: '#c41e3a',
  },
  {
    title: 'Marine Insurance',
    description: 'Hull, cargo, and freight liability cover for vessels navigating inland waterways and international seas.',
    icon: '⚓',
    tag: 'Maritime',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80',
    color: '#7f0000',
  },
  {
    title: 'Aviation Insurance',
    description: 'Comprehensive cover for aircraft owners, operators, and airports — from hull damage to passenger liability.',
    icon: '✈️',
    tag: 'Aerospace',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
    color: '#8b0000',
  },
  {
    title: 'Surety Bonds',
    description: 'Performance, bid, and payment bonds that guarantee your contractual obligations and build client trust.',
    icon: '📜',
    tag: 'Bonds',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
    color: '#a00000',
  },
  {
    title: "Workmen's Compensation",
    description: 'Protect your workforce with statutory and enhanced cover for workplace injuries and occupational disease.',
    icon: '⚙️',
    tag: 'Workforce',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
    color: '#8b0000',
  },
  {
    title: 'Engineering Insurance',
    description: 'Erection all risks, plant machinery, and electronic equipment cover for complex industrial projects.',
    icon: '🏗️',
    tag: 'Industrial',
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80',
    color: '#7f0000',
  },
  {
    title: 'Professional Indemnity',
    description: 'Shield your firm from claims of negligence, errors, or omissions — essential for every service professional.',
    icon: '⚖️',
    tag: 'Liability',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80',
    color: '#8b0000',
  },
]

const steps = [
  { step: '01', title: 'Consultation', description: 'We listen to understand your exposure and risk appetite.', icon: '💬' },
  { step: '02', title: 'Analysis', description: 'Our experts model your risk profile against market benchmarks.', icon: '📊' },
  { step: '03', title: 'Quotation', description: 'You receive competitive, clear, no-surprise quotes.', icon: '📋' },
  { step: '04', title: 'Coverage', description: 'We bind your policy and support you every step after.', icon: '✅' },
]

/* ─────────────────────────────────────────
   HOOKS
───────────────────────────────────────── */
function useParallax(speed = 0.3) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const handle = () => {
      const rect = el.getBoundingClientRect()
      const center = rect.top + rect.height / 2 - window.innerHeight / 2
      const offset = center * speed
      const img = el.querySelector('.parallax-img') as HTMLElement
      if (img) img.style.transform = `translateY(${offset}px) scale(1.15)`
    }
    window.addEventListener('scroll', handle, { passive: true })
    handle()
    return () => window.removeEventListener('scroll', handle)
  }, [speed])
  return ref
}

function useScrollReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('sr-visible'); io.unobserve(e.target) } }),
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    )
    document.querySelectorAll('.sr').forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])
}

/* ─────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────── */
function HeroParallax() {
  const ref = useParallax(0.25)
  return (
    <div ref={ref} className="hero-wrap">
      <div className="parallax-img hero-bg" />
      <div className="hero-overlay" />
      <div className="hero-content">
        <span className="hero-eyebrow sr">— Risk Management Experts —</span>
        <h1 className="hero-title sr">
          Insurance<br /><em>Reimagined.</em>
        </h1>
        <p className="hero-sub sr">
          12 specialist lines. One trusted partner.<br />
          Comprehensive coverage for every chapter of your journey.
        </p>
        <div className="hero-ctas sr">
          <a href="/contact" className="btn-primary">Get a Free Quote</a>
          <a href="#services" className="btn-ghost">Explore Products ↓</a>
        </div>
      </div>
    </div>
  )
}

function ServiceCard({ service, idx }: { service: typeof services[0]; idx: number }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      className={`scard sr`}
      style={{ '--delay': `${(idx % 4) * 80}ms` } as React.CSSProperties}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="scard-img-wrap">
        <img
          src={service.image}
          alt={service.title}
          className="scard-img"
          style={{ transform: hovered ? 'scale(1.08)' : 'scale(1)' }}
          loading="lazy"
        />
        <div className="scard-tag">{service.tag}</div>
        <div className="scard-icon">{service.icon}</div>
      </div>
      <div className="scard-body">
        <h3 className="scard-title">{service.title}</h3>
        <p className="scard-desc">{service.description}</p>
        <a href="/contact" className="scard-link">
          Learn More <span className="scard-arrow">→</span>
        </a>
      </div>
      <div className="scard-accent" style={{ background: service.color }} />
    </div>
  )
}

function StepCard({ item, idx }: { item: typeof steps[0]; idx: number }) {
  return (
    <div className="step-card sr" style={{ '--delay': `${idx * 100}ms` } as React.CSSProperties}>
      <div className="step-icon">{item.icon}</div>
      <div className="step-num">{item.step}</div>
      <h3 className="step-title">{item.title}</h3>
      <p className="step-desc">{item.description}</p>
      {idx < steps.length - 1 && <div className="step-connector" />}
    </div>
  )
}

function BandParallax() {
  const ref = useParallax(0.2)
  return (
    <div ref={ref} className="band-wrap sr">
      <div className="parallax-img band-bg" />
      <div className="band-overlay" />
      <div className="band-content">
        <h2 className="band-title">Coverage You Can Count On</h2>
        <p className="band-sub">Over 20 years protecting individuals, businesses, and industries across Africa and beyond.</p>
        <div className="band-stats">
          {[['2K+', 'Policies Active'], ['98%', 'Claims Settled'], ['12+', 'Years Experience'], ['30+', 'Insurer Partners']].map(([val, label]) => (
            <div key={label} className="band-stat">
              <span className="band-stat-val">{val}</span>
              <span className="band-stat-label">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────
   PAGE
───────────────────────────────────────── */
const Services: React.FC = () => {
  useScrollReveal()

  return (
    <>
      <style>{css}</style>

      <HeroParallax />

      {/* ── Services Grid ── */}
      <section id="services" className="section-services">
        <div className="container">
          <div className="section-header sr">
            <span className="section-eyebrow">What We Offer</span>
            <h2 className="section-title">Our Insurance Products</h2>
            <p className="section-sub">
              From everyday personal cover to complex specialty lines — we have you protected.
            </p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => <ServiceCard key={i} service={s} idx={i} />)}
          </div>
        </div>
      </section>

      {/* ── Parallax Band ── */}
      <BandParallax />

      {/* ── How It Works ── */}
      <section className="section-process">
        <div className="container">
          <div className="section-header sr">
            <span className="section-eyebrow">Simple Steps</span>
            <h2 className="section-title">How It Works</h2>
          </div>
          <div className="steps-row">
            {steps.map((s, i) => <StepCard key={i} item={s} idx={i} />)}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section-cta">
        <div className="container">
          <div className="cta-card sr">
            <div className="cta-bg-img" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1400&q=80')` }} />
            <div className="cta-overlay" />
            <div className="cta-body">
              <span className="cta-eyebrow">Start Today</span>
              <h2 className="cta-title">Ready to Get Protected?</h2>
              <p className="cta-sub">Let our specialists craft the ideal coverage for your situation — no jargon, no pressure.</p>
              <a href="/contact" className="btn-primary btn-lg">Talk to an Expert →</a>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Services

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --brand: #c41e3a;
  --brand-light: #ef5350;
  --dark: #1a1a1a;
  --mid: #2d2d2d;
  --gold: #b0b0b0;
  --offwhite: #f5f5f1;
  --gray: #6b7280;
  --radius: 16px;
  --shadow: 0 8px 40px rgba(0,0,0,0.12);
  --font-display: 'Playfair Display', serif;
  --font-body: 'DM Sans', sans-serif;
  --transition: 0.45s cubic-bezier(0.23, 1, 0.32, 1);
}

body { font-family: var(--font-body); background: var(--offwhite); color: var(--dark); overflow-x: hidden; }

/* ── Scroll Reveal ── */
.sr {
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.7s ease, transform 0.7s ease;
  transition-delay: var(--delay, 0ms);
}
.sr-visible {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* ── Hero ── */
.hero-wrap {
  position: relative;
  height: 100vh;
  min-height: 680px;
  overflow: hidden;
  display: flex;
  align-items: center;
}
.hero-bg {
  position: absolute;
  inset: -15%;
  background-image: url('https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1800&q=80');
  background-size: cover;
  background-position: center;
  will-change: transform;
  transition: transform 0.05s linear;
}
.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(26,26,26,0.88) 0%, rgba(26,26,26,0.55) 60%, rgba(26,26,26,0.2) 100%);
}
.hero-content {
  position: relative;
  z-index: 2;
  padding: 0 6vw;
  max-width: 800px;
}
.hero-eyebrow {
  font-family: var(--font-body);
  font-size: 0.8rem;
  letter-spacing: 0.25em;
  color: var(--gold);
  text-transform: uppercase;
  display: block;
  margin-bottom: 20px;
  opacity: 0;
  animation: fadeUp 0.8s 0.2s forwards;
}
.hero-title {
  font-family: var(--font-display);
  font-size: clamp(3rem, 7vw, 6.5rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.05;
  margin-bottom: 24px;
  opacity: 0;
  animation: fadeUp 0.9s 0.35s forwards;
}
.hero-title em { color: var(--gold); font-style: italic; }
.hero-sub {
  font-size: 1.15rem;
  color: rgba(255,255,255,0.8);
  line-height: 1.7;
  margin-bottom: 40px;
  opacity: 0;
  animation: fadeUp 0.9s 0.5s forwards;
}
.hero-ctas {
  display: flex; gap: 16px; flex-wrap: wrap;
  opacity: 0;
  animation: fadeUp 0.9s 0.65s forwards;
}

/* ── Buttons ── */
.btn-primary {
  display: inline-block;
  padding: 14px 32px;
  background: var(--brand);
  color: #fff;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 6px;
  text-decoration: none;
  transition: background var(--transition), transform 0.2s;
}
.btn-primary:hover { background: #a01830; transform: translateY(-2px); }
.btn-primary.btn-lg { padding: 18px 44px; font-size: 1rem; }
.btn-ghost {
  display: inline-block;
  padding: 14px 28px;
  border: 1.5px solid rgba(255,255,255,0.45);
  color: #fff;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.95rem;
  border-radius: 6px;
  text-decoration: none;
  transition: border-color var(--transition), background var(--transition);
}
.btn-ghost:hover { border-color: #fff; background: rgba(255,255,255,0.08); }

/* ── Layout ── */
.container { max-width: 1280px; margin: 0 auto; padding: 0 5vw; }

.section-header { text-align: center; margin-bottom: 64px; }
.section-eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--brand);
  font-weight: 600;
  display: block;
  margin-bottom: 12px;
}
.section-title {
  font-family: var(--font-display);
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 700;
  color: var(--dark);
  margin-bottom: 16px;
}
.section-sub {
  color: var(--gray);
  font-size: 1.05rem;
  max-width: 580px;
  margin: 0 auto;
  line-height: 1.75;
}

/* ── Services ── */
.section-services { padding: 100px 0; background: var(--offwhite); }
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 28px;
}

/* ── Service Card ── */
.scard {
  position: relative;
  background: #fff;
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  cursor: pointer;
  transition: transform var(--transition), box-shadow var(--transition);
}
.scard:hover { transform: translateY(-8px); box-shadow: 0 24px 60px rgba(0,0,0,0.15); }
.scard-img-wrap {
  position: relative;
  height: 210px;
  overflow: hidden;
}
.scard-img {
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.65s cubic-bezier(0.23, 1, 0.32, 1);
}
.scard-tag {
  position: absolute;
  top: 14px; left: 14px;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(6px);
  color: #fff;
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 999px;
  font-weight: 600;
}
.scard-icon {
  position: absolute;
  bottom: -18px; right: 20px;
  width: 48px; height: 48px;
  background: #fff;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.14);
}
.scard-body {
  padding: 32px 24px 24px;
}
.scard-title {
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--dark);
  margin-bottom: 10px;
}
.scard-desc {
  color: var(--gray);
  font-size: 0.9rem;
  line-height: 1.7;
  margin-bottom: 20px;
}
.scard-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--brand);
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
  transition: gap 0.25s;
}
.scard-link:hover { gap: 10px; }
.scard-arrow { transition: transform 0.25s; }
.scard-link:hover .scard-arrow { transform: translateX(4px); }
.scard-accent {
  position: absolute;
  bottom: 0; left: 0;
  height: 3px; width: 100%;
  opacity: 0;
  transition: opacity 0.35s;
}
.scard:hover .scard-accent { opacity: 1; }

/* ── Parallax Band ── */
.band-wrap {
  position: relative;
  height: 420px;
  overflow: hidden;
  display: flex;
  align-items: center;
}
.band-bg {
  position: absolute;
  inset: -20%;
  background-image: url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80');
  background-size: cover;
  background-position: center;
  will-change: transform;
  transition: transform 0.05s linear;
}
.band-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(120deg, rgba(26,26,26,0.92) 0%, rgba(26,26,26,0.70) 100%);
}
.band-content {
  position: relative; z-index: 2;
  width: 100%; max-width: 1280px;
  margin: 0 auto; padding: 0 5vw;
  text-align: center;
}
.band-title {
  font-family: var(--font-display);
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 12px;
}
.band-sub {
  color: rgba(255,255,255,0.7);
  font-size: 1rem;
  margin-bottom: 48px;
  max-width: 560px;
  margin-left: auto; margin-right: auto;
}
.band-stats {
  display: flex;
  justify-content: center;
  gap: clamp(24px, 6vw, 80px);
  flex-wrap: wrap;
}
.band-stat { text-align: center; }
.band-stat-val {
  display: block;
  font-family: var(--font-display);
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  color: var(--brand-light);
  line-height: 1;
}
.band-stat-label {
  display: block;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.6);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-top: 6px;
}

/* ── Process ── */
.section-process {
  padding: 100px 0;
  background: var(--dark);
}
.section-process .section-eyebrow { color: var(--gold); }
.section-process .section-title { color: #fff; }
.steps-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 32px;
}
.step-card {
  position: relative;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: var(--radius);
  padding: 36px 28px;
  text-align: center;
  transition: background var(--transition), border-color var(--transition), transform var(--transition);
}
.step-card:hover { background: rgba(255,255,255,0.09); border-color: rgba(196,30,58,0.4); transform: translateY(-4px); }
.step-icon { font-size: 2rem; margin-bottom: 12px; }
.step-num {
  font-family: var(--font-display);
  font-size: 3.5rem;
  font-weight: 900;
  color: rgba(196,30,58,0.18);
  line-height: 1;
  position: absolute;
  top: 16px; right: 20px;
}
.step-title {
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
}
.step-desc { color: rgba(255,255,255,0.55); font-size: 0.875rem; line-height: 1.7; }

/* ── CTA ── */
.section-cta { padding: 80px 0 100px; background: var(--offwhite); }
.cta-card {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  min-height: 400px;
  display: flex;
  align-items: center;
}
.cta-bg-img {
  position: absolute; inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 6s ease;
}
.cta-card:hover .cta-bg-img { transform: scale(1.04); }
.cta-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(100deg, rgba(26,26,26,0.92) 40%, rgba(26,26,26,0.5) 100%);
}
.cta-body {
  position: relative; z-index: 2;
  padding: 60px 7vw;
  max-width: 620px;
}
.cta-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--gold);
  font-weight: 600;
  display: block;
  margin-bottom: 16px;
}
.cta-title {
  font-family: var(--font-display);
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 900;
  color: #fff;
  margin-bottom: 16px;
}
.cta-sub {
  color: rgba(255,255,255,0.72);
  font-size: 1rem;
  line-height: 1.75;
  margin-bottom: 36px;
}

/* ── Animation keyframes ── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Responsive tweaks ── */
@media (max-width: 640px) {
  .hero-ctas { flex-direction: column; }
  .band-stats { gap: 32px; }
  .cta-body { padding: 48px 6vw; }
}
`